/**
 * src/components/anomaly/anomalyApi.js
 *
 * Tiny fetch wrappers around /api/anomaly/* on the expenseServer (port 3101).
 *
 * URL resolution:
 *   REACT_APP_ANOMALY_API_URL overrides everything (recommended local pattern:
 *     REACT_APP_ANOMALY_API_URL=http://localhost:3101).
 *   Otherwise we fall back to http://localhost:3101 in dev and an empty prefix
 *   in prod (assumes a reverse proxy in front of both services). Adjust as
 *   the Azure-Functions production path comes online.
 */

const DEFAULT_LOCAL = 'http://localhost:3101';

export const anomalyApiUrl = (() => {
    if (process.env.REACT_APP_ANOMALY_API_URL) return process.env.REACT_APP_ANOMALY_API_URL.replace(/\/$/, '');
    if (process.env.NODE_ENV !== 'production') return DEFAULT_LOCAL;
    return ''; // proxy / same origin in prod
})();

async function get(path, params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== ''));
    const url = `${anomalyApiUrl}/api/anomaly${path}${qs.toString() ? `?${qs}` : ''}`;
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) throw new Error(`GET ${path} -> ${r.status}`);
    return r.json();
}

async function post(path, body) {
    const url = `${anomalyApiUrl}/api/anomaly${path}`;
    const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
        credentials: 'omit',
    });
    if (!r.ok) throw new Error(`POST ${path} -> ${r.status}`);
    return r.json();
}

// flags
export const listFlags = (filters) => get('/flags/list', filters);
export const getFlagCounts = (manager) => get('/flags/counts', { manager });
export const getFlag = (id) => get(`/flags/${encodeURIComponent(id)}`);
export const getFlagSource = (id) => get(`/flags/${encodeURIComponent(id)}/source`);
export const setFlagStatus = (id, body) => post(`/flags/${encodeURIComponent(id)}/status`, body);

// reports
export const biggestSpenders = (params) => get('/reports/biggest-spenders', params);
export const flagVolumeByRule = (params) => get('/reports/flag-volume-by-rule', params);
export const flagVolumeByManager = (params) => get('/reports/flag-volume-by-manager', params);
export const flagVolumeTimeseries = (days) => get('/reports/flag-volume-timeseries', { days });
export const fuelCostPerCrew = (params) => get('/reports/fuel-cost-per-crew', params);

// catalog
export const rulesCatalog = () => get('/rules');

// AI Q&A
export const aiChat = async (body) => {
    const r = await fetch(`${anomalyApiUrl}/api/anomaly/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
        credentials: 'omit',
    });
    if (!r.ok) throw new Error(`POST /ai/chat -> ${r.status}`);
    return r.json();
};
